import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiX, FiTrash2, FiCornerDownLeft, FiLink } from 'react-icons/fi';
import './SQLHistory.css';
import memoWindow from './common/memoWindow';

/**
 * SQL 실행이력 — 오른쪽 패널(제목줄은 HistoryAndChallengeTabs 의 PanelHeader).
 * 모양은 데이터 분석 패널과 같은 계열이다(2026-10-19 사용자 요청): 위에 한 줄 설명 띠, 촘촘한 목록, 누르면 아래에 상세.
 * 실행일시는 날짜를 줄여(월-일 시:분:초, 전체는 툴팁) 칸을 좁히고, 상태는 글자 대신 표시(✓ / ✕)로 둔다.
 */
const pad = (n) => String(n).padStart(2, '0');
const fullDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const shortDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
/** 1초 미만은 ms(정수), 그 위는 초(소수 둘째 자리) */
const elapsed = (ms) => {
    if (ms == null || Number.isNaN(Number(ms))) return '';
    const v = Number(ms);
    return v < 1000 ? `${Math.round(v)}ms` : `${(v / 1000).toFixed(2)}s`;
};
const oneLine = (q) => (q ? q.replace(/\s+/g, ' ').trim() : '');

const SQLHistory = ({ history, onHistoryClick, clearHistory, deleteHistoryItem }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(null);
    const items = Array.isArray(history) ? history : [];

    const renderBinds = (binds) => Object.entries(binds).map(([key, value]) => {
        // statement 별로 묶인 새 구조와 단순 값(옛 구조)을 함께 읽는다
        if (value && typeof value === 'object') {
            return (
                <div key={key} className="sh-bind-group">
                    <span className="sh-bind-stmt">{key}</span>
                    {Object.entries(value).map(([name, v]) => (
                        <span key={`${key}_${name}`} className="sh-bind"><code>:{name}</code> = <code>{String(v)}</code></span>
                    ))}
                </div>
            );
        }
        return <span key={key} className="sh-bind"><code>:{key}</code> = <code>{String(value)}</code></span>;
    });

    return (
        <div className="sh">
            {/* 한 줄 설명 + 전체 삭제 — 데이터 분석의 설명 띠와 같은 자리·모양 */}
            <div className="sh-subtitle">
                <span className="sh-subtitle-text">{t('common:hist.summary', { n: items.length })}</span>
                {items.length > 0 && (
                    <button type="button" className="sh-btn" onClick={clearHistory} title={t('common:hist.clearAll')}>
                        <FiTrash2 size={11} /> {t('common:hist.clearAllShort')}
                    </button>
                )}
            </div>

            {items.length === 0 ? (
                <div className="sh-empty">{t('common:hist.empty')}</div>
            ) : (
                <div className="sh-list" role="table" aria-label={t('common:hist.title')}>
                    <div className="sh-row sh-head" role="row">
                        <span className="sh-c-time" role="columnheader">{t('common:hist.executedAt')}</span>
                        <span className="sh-c-status" role="columnheader">{t('common:hist.status')}</span>
                        <span className="sh-c-query" role="columnheader">{t('common:hist.query')}</span>
                        <span className="sh-c-count" role="columnheader" title={t('common:hist.fetchCount')}>{t('common:hist.rowsShort')}</span>
                        <span className="sh-c-elapsed" role="columnheader">{t('common:hist.elapsed')}</span>
                        <span className="sh-c-del" aria-hidden="true" />
                    </div>
                    <div className="sh-body">
                        {items.map((item, index) => (
                            <div
                                key={item.timestamp || index}
                                role="row"
                                className={`sh-row${item.success ? '' : ' failed'}${selected === item ? ' selected' : ''}`}
                                onClick={() => setSelected(item)}
                                title={t('common:hist.clickForFull')}
                            >
                                <span className="sh-c-time" title={fullDate(item.timestamp)}>{shortDate(item.timestamp)}</span>
                                <span className="sh-c-status" title={item.success ? t('common:hist.success') : t('common:hist.failed')}>
                                    <span className={`sh-dot ${item.success ? 'ok' : 'err'}`}>
                                        {item.success ? <FiCheck size={10} /> : <FiX size={10} />}
                                    </span>
                                </span>
                                <span className="sh-c-query">{oneLine(item.query)}</span>
                                <span className="sh-c-count">
                                    {item.success ? (
                                        <>
                                            {t('common:hist.nRows', { n: item.fetchCount || 0 })}
                                            {item.hasMore ? <span className="sh-more">+</span> : null}
                                        </>
                                    ) : '-'}
                                </span>
                                <span className="sh-c-elapsed">{elapsed(item.executionTime)}</span>
                                <span className="sh-c-del">
                                    <button
                                        type="button"
                                        className="sh-del"
                                        onClick={(e) => {
                                            e.stopPropagation();   // 행 선택과 갈라 둔다
                                            deleteHistoryItem(item.timestamp);
                                            if (selected === item) setSelected(null);
                                        }}
                                        title={t('common:hist.deleteItem')}
                                        aria-label={t('common:hist.deleteItem')}
                                    >
                                        <FiX size={12} />
                                    </button>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 고른 항목의 상세 — 전체 SQL · 바인드 값 · 오류 */}
            {selected && (
                <div className="sh-detail">
                    <div className="sh-detail-head">
                        <span className="sh-detail-title">{t('common:hist.detail')}</span>
                        <button type="button" className="sh-icon" onClick={() => setSelected(null)} title={t('common:hist.close')} aria-label={t('common:hist.close')}>
                            <FiX size={13} />
                        </button>
                    </div>
                    <div className="sh-meta">
                        <span className="sh-chip">{fullDate(selected.timestamp)}</span>
                        <span className={`sh-chip ${selected.success ? 'ok' : 'err'}`}>{selected.success ? t('common:hist.success') : t('common:hist.failed')}</span>
                        {selected.success && <span className="sh-chip">{t('common:hist.nRowsReturned', { n: selected.fetchCount || 0 })}</span>}
                        {selected.success && <span className="sh-chip">{elapsed(selected.executionTime)}</span>}
                        {selected.bindVariables && <span className="sh-chip"><FiLink size={10} /> {t('common:hist.bindVarsShort')}</span>}
                    </div>
                    {!selected.success && selected.error && (
                        <div className="sh-error"><strong>{t('common:hist.error')}</strong> {selected.error}</div>
                    )}
                    {selected.bindVariables && (
                        <div className="sh-binds"><span className="sh-label">{t('common:hist.bindVars')}</span>{renderBinds(selected.bindVariables)}</div>
                    )}
                    {selected.originalQuery && selected.originalQuery !== selected.query && (
                        <>
                            <div className="sh-label">{t('common:hist.originalQuery')}</div>
                            <pre className="sh-sql">{selected.originalQuery}</pre>
                        </>
                    )}
                    <div className="sh-label">
                        {selected.originalQuery && selected.originalQuery !== selected.query ? t('common:hist.executedSubstituted') : t('common:hist.executedQuery')}
                    </div>
                    <pre className="sh-sql">{selected.query}</pre>
                    <div className="sh-actions">
                        <button
                            type="button"
                            className="sh-btn"
                            onClick={() => {
                                // 원본 쿼리가 있으면 원본을, 없으면 실행된 쿼리를 커서 위치에 넣는다
                                onHistoryClick(selected.originalQuery || selected.query);
                                setSelected(null);
                            }}
                            title={t('common:hist.insertOriginalHint')}
                        >
                            <FiCornerDownLeft size={11} /> {t('common:hist.insertOriginal')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

SQLHistory.propTypes = {
    history: PropTypes.array,
    onHistoryClick: PropTypes.func.isRequired,
    clearHistory: PropTypes.func.isRequired,
    deleteHistoryItem: PropTypes.func.isRequired,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SQLHistory);
